import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../config/axios';
import { AIInstruction, AIInstructionFormData, AIInstructionStats } from '../../types/aiInstruction';

interface AIInstructionState {
  keywords: AIInstruction[];
  currentKeyword: AIInstruction | null;
  stats: AIInstructionStats | null;
  loading: boolean;
  error: string | null;
}

const initialState: AIInstructionState = {
  keywords: [],
  currentKeyword: null,
  stats: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchKeywords = createAsyncThunk(
  'aiInstructions/fetchKeywords',
  async (params: { search?: string; active?: boolean } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.search) queryParams.append('search', params.search);
      if (params?.active !== undefined) queryParams.append('active', params.active.toString());
      
      const response = await api.get(`/ai-instructions?${queryParams.toString()}`);
      return response.data.keywords;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch AI instruction keywords');
    }
  }
);

export const fetchKeywordStats = createAsyncThunk(
  'aiInstructions/fetchKeywordStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/ai-instructions/stats');
      return response.data.stats;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch statistics');
    }
  }
);

export const fetchKeywordById = createAsyncThunk(
  'aiInstructions/fetchKeywordById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/ai-instructions/${id}`);
      return response.data.keyword;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch keyword');
    }
  }
);

export const createKeyword = createAsyncThunk(
  'aiInstructions/createKeyword',
  async (data: AIInstructionFormData, { rejectWithValue }) => {
    try {
      const response = await api.post('/ai-instructions', data);
      return response.data.keyword;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create keyword');
    }
  }
);

export const updateKeyword = createAsyncThunk(
  'aiInstructions/updateKeyword',
  async ({ id, data }: { id: number; data: Partial<AIInstructionFormData> }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/ai-instructions/${id}`, data);
      return response.data.keyword;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update keyword');
    }
  }
);

export const toggleKeywordActive = createAsyncThunk(
  'aiInstructions/toggleKeywordActive',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/ai-instructions/${id}/toggle`);
      return response.data.keyword;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to toggle keyword');
    }
  }
);

export const deleteKeyword = createAsyncThunk(
  'aiInstructions/deleteKeyword',
  async (id: number, { rejectWithValue }) => {
    try {
      await api.delete(`/ai-instructions/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete keyword');
    }
  }
);

const aiInstructionSlice = createSlice({
  name: 'aiInstructions',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentKeyword: (state, action: PayloadAction<AIInstruction | null>) => {
      state.currentKeyword = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch keywords
      .addCase(fetchKeywords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKeywords.fulfilled, (state, action) => {
        state.loading = false;
        state.keywords = action.payload;
      })
      .addCase(fetchKeywords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch stats
      .addCase(fetchKeywordStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKeywordStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchKeywordStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch keyword by ID
      .addCase(fetchKeywordById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKeywordById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentKeyword = action.payload;
      })
      .addCase(fetchKeywordById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create keyword
      .addCase(createKeyword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createKeyword.fulfilled, (state, action) => {
        state.loading = false;
        state.keywords.unshift(action.payload);
      })
      .addCase(createKeyword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update keyword
      .addCase(updateKeyword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateKeyword.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.keywords.findIndex(k => k.id === action.payload.id);
        if (index !== -1) {
          state.keywords[index] = action.payload;
        }
        if (state.currentKeyword?.id === action.payload.id) {
          state.currentKeyword = action.payload;
        }
      })
      .addCase(updateKeyword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Toggle keyword active
      .addCase(toggleKeywordActive.pending, (state) => {
        state.error = null;
      })
      .addCase(toggleKeywordActive.fulfilled, (state, action) => {
        const index = state.keywords.findIndex(k => k.id === action.payload.id);
        if (index !== -1) {
          state.keywords[index] = action.payload;
        }
        if (state.currentKeyword?.id === action.payload.id) {
          state.currentKeyword = action.payload;
        }
      })
      .addCase(toggleKeywordActive.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Delete keyword
      .addCase(deleteKeyword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteKeyword.fulfilled, (state, action) => {
        state.loading = false;
        state.keywords = state.keywords.filter(k => k.id !== action.payload);
        if (state.currentKeyword?.id === action.payload) {
          state.currentKeyword = null;
        }
      })
      .addCase(deleteKeyword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentKeyword } = aiInstructionSlice.actions;
export default aiInstructionSlice.reducer;

